import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard,
  Ticket,
  BarChart3,
  Users,
  Layers,
  Timer,
  Mail,
  MessageCircle,
  Building2,
  History,
  Bell,
  LogOut,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  ClipboardList,
  CalendarClock,
  X,
} from "lucide-react";
import Logo from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, ROLES, hasPermission } from "@/lib/constants";

const NAV = [
  {
    to: "/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    testid: "nav-dashboard",
  },
  {
    to: "/tickets",
    label: "Tickets",
    icon: Ticket,
    permission: "view_tickets",
    testid: "nav-tickets",
  },
  {
    to: "/reports",
    label: "Reports",
    icon: BarChart3,
    testid: "nav-reports",
  },
  {
    to: "/notifications",
    label: "Notifications",
    icon: Bell,
    testid: "nav-notifications",
  },
];

const ADMIN_NAV = [
  {
    to: "/admin/users",
    label: "Users",
    icon: Users,
    permission: "manage_users",
    roles: [
      ROLES.SUPER_ADMIN,
      ROLES.SUPPORT_ADMIN,
      ROLES.BUSINESS_ADMIN,
      ROLES.CLIENT_ADMIN,
    ],
    testid: "nav-admin-users",
  },

  {
    to: "/admin/organizations",
    label: "Clients",
    icon: Building2,
    roles: [
      ROLES.SUPER_ADMIN,
      ROLES.SUPPORT_ADMIN,
      ROLES.BUSINESS_ADMIN,
    ],
    testid: "nav-admin-organizations",
  },
  {
    to: "/admin/applications",
    label: "Applications",
    icon: Layers,
    permission: "manage_applications",
    roles: [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN],
    testid: "nav-admin-applications",
  },
  {
    to: "/admin/sla",
    label: "SLA Policies",
    icon: Timer,
    permission: "manage_sla",
    roles: [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN],
    testid: "nav-admin-sla",
  },
  {
    to: "/admin/departments",
    label: "Departments",
    icon: Building2,
    roles: [ROLES.SUPER_ADMIN, ROLES.BUSINESS_ADMIN],
    testid: "nav-admin-departments",
  },
  {
    to: "/admin/roles",
    label: "Roles & Permissions",
    icon: KeyRound,
    roles: [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN],
    testid: "nav-admin-roles",
  },
  {
    to: "/admin/smtp",
    label: "SMTP",
    icon: Mail,
    permission: "configure_smtp",
    roles: [ROLES.SUPER_ADMIN],
    testid: "nav-admin-smtp",
  },
  {
    to: "/admin/whatsapp",
    label: "WhatsApp",
    icon: MessageCircle,
    permission: "configure_whatsapp",
    roles: [ROLES.SUPER_ADMIN],
    testid: "nav-admin-whatsapp",
  },
  {
    to: "/admin/notification-logs",
    label: "Notification Logs",
    icon: ClipboardList,
    permission: "view_notification_logs",
    roles: [ROLES.SUPER_ADMIN],
    testid: "nav-admin-notiflogs",
  },
  {
    to: "/admin/audit",
    label: "Audit Log",
    icon: History,
    permission: "view_audit",
    roles: [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN],
    testid: "nav-admin-audit",
  },
];

export default function Sidebar({ mobileOpen = false, onMobileClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  if (!user) return null;

  const allowed = (item) =>
    !item.permission || hasPermission(user, item.permission);
  const navItems = NAV.filter(allowed);
  const adminItems = ADMIN_NAV.filter(
    (item) => item.roles.includes(user.role) && allowed(item)
  );

  const closeMobileMenu = () => {
    if (typeof onMobileClose === "function") {
      onMobileClose();
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label="Close navigation overlay"
        onClick={closeMobileMenu}
        className={`fixed inset-0 z-40 bg-black/65 backdrop-blur-sm transition-opacity duration-200 lg:hidden ${
          mobileOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        }`}
      />

      <aside
        data-testid="app-sidebar"
        className={`glass fixed inset-y-0 left-0 z-50 flex h-[100dvh] w-[min(86vw,320px)] flex-col border-r border-border transition-transform duration-200 ease-out lg:sticky lg:top-0 lg:z-20 lg:h-screen lg:flex-shrink-0 lg:translate-x-0 lg:transition-[width] ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } ${collapsed ? "lg:w-[76px]" : "lg:w-[260px]"}`}
      >
        <div
          className={`flex min-h-[70px] items-center gap-2 border-b border-border px-4 py-4 ${
            collapsed ? "lg:justify-center" : ""
          }`}
        >
          <div className="min-w-0 flex-1 lg:flex-none">
            {collapsed ? (
              <div className="hidden h-9 w-9 items-center justify-center rounded-md bg-gradient-to-br from-[#d4af37] to-[#a17e1c] font-display font-bold text-black lg:flex">
                M
              </div>
            ) : (
              <Logo size={30} showText />
            )}

            {collapsed && (
              <div className="lg:hidden">
                <Logo size={30} showText />
              </div>
            )}
          </div>

          <button
            type="button"
            aria-label="Close sidebar"
            onClick={closeMobileMenu}
            className="ml-auto flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-[#d4af37]/40 hover:text-[#e5c158] lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-4 scrollbar-thin">
          <NavSection
            label={!collapsed ? "Workspace" : null}
            items={navItems}
            collapsed={collapsed}
            onNavigate={closeMobileMenu}
          />

          {adminItems.length > 0 && (
            <NavSection
              label={!collapsed ? "Administration" : null}
              items={adminItems}
              collapsed={collapsed}
              onNavigate={closeMobileMenu}
            />
          )}
        </nav>

        <div className="space-y-2 border-t border-border p-3">
          <NavLink
            to="/profile"
            onClick={closeMobileMenu}
            data-testid="sidebar-profile-link"
            className={`flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-white/[0.03] ${
              collapsed ? "lg:justify-center" : ""
            }`}
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#d4af37] to-[#8a6b1e] text-xs font-semibold text-black">
              {(user.name || user.email || "?").slice(0, 2).toUpperCase()}
            </div>

            <div
              className={`min-w-0 flex-1 ${
                collapsed ? "lg:hidden" : ""
              }`}
            >
              <div
                className="truncate text-xs font-semibold"
                data-testid="sidebar-user-name"
              >
                {user.name || user.email}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {ROLE_LABEL[user.role]}
              </div>
            </div>
          </NavLink>

          <button
            type="button"
            data-testid="logout-button"
            onClick={async () => {
              closeMobileMenu();
              await logout();
              navigate("/login");
            }}
            className={`flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-xs transition-colors hover:border-[#d4af37]/50 hover:text-[#e5c158] ${
              collapsed ? "lg:justify-center" : ""
            }`}
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            <span className={collapsed ? "lg:hidden" : ""}>Sign out</span>
          </button>

          <button
            type="button"
            data-testid="sidebar-toggle"
            onClick={() => setCollapsed((current) => !current)}
            className="hidden w-full items-center justify-center rounded-md py-1 text-muted-foreground hover:text-foreground lg:flex"
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>
      </aside>
    </>
  );
}

function NavSection({
  label,
  items,
  collapsed,
  onNavigate,
}) {
  return (
    <div className="mb-4">
      {label && (
        <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          {label}
        </div>
      )}

      <ul className="space-y-0.5">
        {items.map((item) => {
          const Icon = item.icon;

          return (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={onNavigate}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md border px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "border-[#d4af37]/25 bg-[#d4af37]/10 text-[#e5c158]"
                      : "border-transparent text-muted-foreground hover:bg-white/[0.03] hover:text-foreground"
                  } ${collapsed ? "lg:justify-center" : ""}`
                }
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span
                  className={`truncate ${
                    collapsed ? "lg:hidden" : ""
                  }`}
                >
                  {item.label}
                </span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
